import { useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import { CRM_SCORE_TONES, CRM_TIPO_LABELS } from '@/pages/painel-pedidos/types';

interface EmpresaCard {
  id: number;
  nome: string;
  cnpj: string | null;
  razao_social: string | null;
  cidade: string | null;
  uf: string | null;
  segmento: string | null;
  tipo: string | null;
  status: string;
  score: number;
  classificacao: string;
  valor_potencial: number | null;
  nivel_confianca: string | null;
  contatos: { id: number }[];
  oportunidades: { id: number; etapa: string }[];
  created_at: string;
}

const STATUS_OPTIONS = [
  { key: 'todos', label: 'Todos' },
  { key: 'cliente', label: 'Clientes' },
  { key: 'crm', label: 'Em CRM' },
];

const CLASS_OPTIONS = ['quente', 'prioritario', 'acompanhar', 'baixa'];

function formatBRL(value: number | null | undefined): string {
  if (value == null) return '—';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatCNPJ(cnpj: string | null | undefined): string {
  if (!cnpj) return '';
  const d = cnpj.replace(/\D/g, '');
  if (d.length !== 14) return cnpj;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

export default function ClientesModule() {
  const { password } = useOutletContext<{ password: string }>();

  const [empresas, setEmpresas] = useState<EmpresaCard[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [segmentoFilter, setSegmentoFilter] = useState('todos');
  const [classFilter, setClassFilter] = useState('todos');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('crm-core', {
        body: { action: 'list_empresas', password },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as { ok?: boolean; empresas?: EmpresaCard[]; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setEmpresas(parsed?.empresas ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar clientes.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password]);

  const segmentos = useMemo(() => {
    const set = new Set<string>();
    empresas.forEach((e) => e.segmento && set.add(e.segmento));
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [empresas]);

  const filtrados = useMemo(() => {
    const q = search.trim().toLowerCase();
    return empresas.filter((e) => {
      if (statusFilter === 'cliente' && e.status !== 'cliente') return false;
      if (statusFilter === 'crm' && e.status === 'cliente') return false;
      if (segmentoFilter !== 'todos' && e.segmento !== segmentoFilter) return false;
      if (classFilter !== 'todos' && e.classificacao !== classFilter) return false;
      if (q) {
        const alvo = [e.nome, e.razao_social, e.cnpj, e.cidade, e.uf, e.segmento]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!alvo.includes(q)) return false;
      }
      return true;
    });
  }, [empresas, search, statusFilter, segmentoFilter, classFilter]);

  const kpis = useMemo(() => {
    const clientes = empresas.filter((e) => e.status === 'cliente').length;
    const emCrm = empresas.filter((e) => e.status !== 'cliente').length;
    const potencial = empresas.reduce((s, e) => s + Number(e.valor_potencial ?? 0), 0);
    const ganhas = empresas.filter((e) =>
      e.oportunidades?.some((o) => o.etapa === 'ganho'),
    ).length;
    return { total: empresas.length, clientes, emCrm, potencial, ganhas };
  }, [empresas]);

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-[#E8610A]/10 text-[#E8610A] rounded-xl">
            <i className="ri-building-4-line text-lg"></i>
          </div>
          <div>
            <h2 className="font-bold text-gray-900">Clientes</h2>
            <p className="text-sm text-gray-500">
              Ficha 360º de empresas, contatos e relacionamento comercial.
            </p>
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60"
        >
          <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
          Atualizar
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Empresas</p>
          <p className="text-2xl font-bold text-gray-900">{kpis.total}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Clientes</p>
          <p className="text-2xl font-bold text-green-600">{kpis.clientes}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Em CRM</p>
          <p className="text-2xl font-bold text-[#E8610A]">{kpis.emCrm}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Potencial total</p>
          <p className="text-lg font-bold text-gray-900 truncate">{formatBRL(kpis.potencial)}</p>
        </div>
      </div>

      {/* Busca e filtros */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4 space-y-4">
        <div className="relative max-w-md">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome, CNPJ, cidade ou segmento..."
            className="w-full border border-gray-200 rounded-lg pl-9 pr-4 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] focus:ring-2 focus:ring-[#E8610A]/20 transition-all bg-white"
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-3 sm:items-center flex-wrap">
          <div className="inline-flex items-center bg-gray-100 rounded-full px-1 py-1">
            {STATUS_OPTIONS.map((s) => (
              <button
                key={s.key}
                onClick={() => setStatusFilter(s.key)}
                className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 transition-colors cursor-pointer whitespace-nowrap ${
                  statusFilter === s.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          <select
            value={segmentoFilter}
            onChange={(e) => setSegmentoFilter(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white cursor-pointer focus:outline-none focus:border-[#E8610A]"
          >
            <option value="todos">Todos os segmentos</option>
            {segmentos.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>

          <select
            value={classFilter}
            onChange={(e) => setClassFilter(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white cursor-pointer focus:outline-none focus:border-[#E8610A]"
          >
            <option value="todos">Todas as prioridades</option>
            {CLASS_OPTIONS.map((c) => (
              <option key={c} value={c}>{CRM_SCORE_TONES[c]?.label ?? c}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Estado */}
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          {error}
        </div>
      )}

      {loading && empresas.length === 0 ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtrados.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-2xl border border-gray-200">
          <div className="w-14 h-14 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-3">
            <i className="ri-contacts-line text-xl text-gray-400"></i>
          </div>
          <p className="text-sm text-gray-500 mb-1">Nenhuma empresa encontrada.</p>
          <p className="text-xs text-gray-400">
            Empresas cadastradas no CRM aparecem automaticamente aqui.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtrados.map((e) => {
            const scoreInfo = CRM_SCORE_TONES[e.classificacao];
            const etapa = e.oportunidades?.[0]?.etapa;
            const ganha = etapa === 'ganho';
            const perdida = etapa === 'perdido';
            return (
              <Link
                key={e.id}
                to={`/painel/clientes/${e.id}`}
                className="group bg-white rounded-2xl border border-gray-200 p-5 hover:border-[#E8610A]/40 transition-colors duration-150 cursor-pointer"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${
                        ganha
                          ? 'bg-green-50 text-green-600'
                          : perdida
                            ? 'bg-red-50 text-red-500'
                            : 'bg-[#E8610A]/10 text-[#E8610A]'
                      }`}
                    >
                      <i className={`${ganha ? 'ri-check-double-line' : perdida ? 'ri-close-line' : 'ri-building-4-line'} text-lg`}></i>
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate group-hover:text-[#E8610A] transition-colors">
                        {e.nome}
                      </h3>
                      {e.cnpj && (
                        <p className="text-xs text-gray-500 truncate">{formatCNPJ(e.cnpj)}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-3 flex items-center gap-1.5 flex-wrap">
                  {scoreInfo && (
                    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5 border ${scoreInfo.tone}`}>
                      <i className={scoreInfo.icon}></i>
                      {scoreInfo.label}
                    </span>
                  )}
                  {e.tipo && (
                    <span className="text-[11px] font-semibold text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">
                      {CRM_TIPO_LABELS[e.tipo] ?? e.tipo}
                    </span>
                  )}
                  {e.status === 'cliente' && (
                    <span className="text-[11px] font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
                      Cliente
                    </span>
                  )}
                </div>

                <div className="mt-4 text-xs text-gray-500 space-y-1">
                  {(e.cidade || e.uf) && (
                    <div className="flex items-center gap-1.5">
                      <i className="ri-map-pin-line"></i>
                      {[e.cidade, e.uf].filter(Boolean).join(' - ')}
                    </div>
                  )}
                  {e.segmento && (
                    <div className="flex items-center gap-1.5 truncate">
                      <i className="ri-store-2-line"></i>
                      {e.segmento}
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-team-line"></i>
                      {e.contatos?.length ?? 0}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-rocket-2-line"></i>
                      {e.oportunidades?.length ?? 0}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Potencial</p>
                    <p className="text-sm font-bold text-gray-900">{formatBRL(e.valor_potencial)}</p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}